import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useWindowWidth } from '../hooks/useDebounce';
import { PDFDocument as PDFLibDocument, rgb } from 'pdf-lib';
import { Document, Page, pdfjs } from 'react-pdf';
import '@react-pdf/renderer';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'; // Import the required CSS file
import "react-pdf/dist/esm/Page/TextLayer.css";
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import fontkit from '@pdf-lib/fontkit';
import '../index.css';
import { FaEdit } from 'react-icons/fa';
import Slider from '@mui/material/Slider';
import { Typography, TextField, Autocomplete, Checkbox, FormControlLabel, Box, Select, MenuItem, Tabs, Tab, FormControl, InputLabel, ToggleButtonGroup, ToggleButton } from '@mui/material';
import SignatureModal from "./SignatureModal.jsx";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import axios from "axios";
import SimpleSnackbar from './SimpleSnackbar.jsx';
import { InlineSpinner, AIAnalysisSpinner } from './loading';
import Draggable from 'react-draggable';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { useDirectionContext } from '../contexts/DirectionContext';

import FileRequestForm from './FileRequestForm';
import { extractTextWithCoordinates, groupTextIntoLines, detectFieldLabels } from '../services/pdfTextExtraction';
import { analyzePDFWithAI } from '../services/aiFieldDetection';
import AISuggestedFieldsCompact from './AISuggestedFieldsCompact';
import { colors, spacing, typography, shadows, radius, transitions } from '../theme/tokens';


pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// TabPanel component for tabbed interface
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`template-tabpanel-${index}`}
      aria-labelledby={`template-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ padding: '24px 16px 0 16px' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Helper: Checkbox field detection (consolidated from lines 970-993 and 1515-1529)
// Single source of truth for checkbox field types
const CHECKBOX_FIELD_TYPES = new Set([
  'receivedFullVaccination',
  'allowsPhotographingInternal',
  'allowsPhotographingExternal',
  'האם ההורים מאשרים לצילום הילד פנימי',
  'האם ההורים מאשרים לצילום הילד חוץ',
  'אלרגיה לתרופות',
  'סכום תשלום חודשה',
]);

const isCheckboxField = (inputType) => {
  if (!inputType) return false;

  return CHECKBOX_FIELD_TYPES.has(inputType.value) ||
         CHECKBOX_FIELD_TYPES.has(inputType.label) ||
         inputType.label?.includes('מאשרים') ||
         inputType.label?.includes('אלרגיה') ||
         inputType.value?.includes('מאשרים') ||
         inputType.value?.includes('אלרגיה');
};

// Helper: Determine which controls to show in field editor (AIF-48)
// Returns visibility flags for each control section based on field type
// This implements progressive disclosure to reduce cognitive load
const getFieldControlVisibility = (inputType, renderType, optionsLength) => {
  if (!inputType) {
    return {
      showDimensions: true,
      showOptions: false,
      showMandatory: true,
      showRenderTypeToggle: false,
    };
  }

  const fieldTypeValue = inputType.value || '';
  const fieldTypeLabel = inputType.label || '';

  const isSignature = fieldTypeValue === 'signature' || fieldTypeValue.includes('signature');
  const isCheckbox = isCheckboxField(inputType);
  const isDate = fieldTypeValue === 'dateOfBirth' || fieldTypeValue === 'signingDate';
  const isId = fieldTypeValue === 'childId' || fieldTypeValue === 'parentId1' || fieldTypeValue === 'parentId2';

  // Legacy: fields saved before renderType was introduced
  const isLegacySelect = fieldTypeValue.includes('select') || fieldTypeValue.includes('dropdown') ||
                         fieldTypeLabel.includes('select') || fieldTypeLabel.includes('dropdown');

  // Dropdown mode: explicit toggle, legacy select type, or field already has options
  const isDropdownMode = renderType === 'dropdown' || isLegacySelect || (optionsLength > 0);

  return {
    showDimensions: true,
    showOptions: isDropdownMode,
    showMandatory: !isSignature,
    // Show render type toggle for all fields except signature, checkbox, date, ID
    showRenderTypeToggle: !isSignature && !isCheckbox && !isDate && !isId,
  };
};

// Helper: Get field type styling (color, icon, border)
// Provides visual distinction between different field types
// Note: This is a pure function that returns style config
// Labels are localized when rendered in the component
const getFieldTypeStyle = (inputType) => {
  if (!inputType) return null;

  // Signature fields
  if (inputType.value === 'signature') {
    return {
      border: '2px solid #10b981',
      backgroundColor: 'rgba(16, 185, 129, 0.08)',
      icon: '✍️',
      color: '#10b981',
      labelKey: 'createTemplate.fieldTypes.signature',
    };
  }

  // Checkbox fields
  if (isCheckboxField(inputType)) {
    return {
      border: '2px solid #3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.08)',
      icon: '☑️',
      color: '#3b82f6',
      labelKey: 'createTemplate.fieldTypes.checkbox',
    };
  }

  // Date fields
  if (inputType.value?.includes('date') ||
      inputType.value?.includes('Date') ||
      inputType.value?.includes('תאריך') ||
      inputType.label?.includes('תאריך')) {
    return {
      border: '2px solid #f59e0b',
      backgroundColor: 'rgba(245, 158, 11, 0.08)',
      icon: '📅',
      color: '#f59e0b',
      labelKey: 'createTemplate.fieldTypes.date',
    };
  }

  // Default: Text fields
  return {
    border: '2px solid #6ab3eb',
    backgroundColor: 'rgba(106, 179, 235, 0.08)',
    icon: '📝',
    color: '#6ab3eb',
    labelKey: 'createTemplate.fieldTypes.text',
  };
};

export default function CreateTemplate(props) {
  const { t } = useTranslation();
  const direction = useDirectionContext();

  const location = useLocation();
  const currentPath = location.pathname;
  const currentProtocol = window.location.protocol;
  const currentDomain = window.location.hostname;
  const currentPort = window.location.port;

  const [inputFields, setInputFields] = useState([]);
  const [extraInputFields, setExtraInputFields] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [containerBounds, setContainerBounds] = useState([]);


  // Helper: Count current signature fields to assign next index
  const getNextSignatureIndex = (inputFields, extraInputFields) => {
    const allFields = [...inputFields, ...extraInputFields];
    const signatureFields = allFields.filter(field =>
      field.editor?.inputType?.value === 'signature'
    );
    return signatureFields.length;
  };

  const [clicks, setClicks] = useState([]);
  const [clickX, setClickX] = useState(null);
  const [clickY, setClickY] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const containerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageWidth, setPageWidth] = useState(null);
  const [pageHeight, setPageHeight] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [templateLink, setTemplateLink] = useState("");
  const [linkFetched, setLinkFetched] = useState(false);
  const [requireID, setRequireID] = useState(false);
  const [requestedFiles, setRequestedFiles] = useState([]);

  const emptySignature = useState("data:image/png;base64,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");

  const [addingTextInputField, SetAddingTextInputField] = useState(false);
  const [addingExtraTextInputField, SetAddingExtraTextInputField] = useState(false);
  const [addingSignatureInputField, SetAddingSignatureInputField] = useState(false);
  const [selectedFieldType, setSelectedFieldType] = useState('text'); // AIF-54: Field type selector

  // AI Field Detection States (AIF-7)
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiError, setAiError] = useState(null);
  const [showAiSuggestions, setShowAiSuggestions] = useState(false);
  const [acceptedSuggestions, setAcceptedSuggestions] = useState([]);
  const [rejectedSuggestions, setRejectedSuggestions] = useState([]);
  const [showDebugMarkers, setShowDebugMarkers] = useState(true); // DEBUG: Show AI coordinate markers
  const [calibrationMode, setCalibrationMode] = useState(false); // Calibration mode
  const [calibrationData, setCalibrationData] = useState(null); // Store calibration info
  const [detectedBlankLines, setDetectedBlankLines] = useState([]); // Store detected underscore positions
  const [activeTab, setActiveTab] = useState(0); // Tab navigation: 0 = PDF Editor, 1 = Required Documents, 2 = Settings

  const pdfFile = 'parents_agreement_fixed.pdf';
  const fontFileUrl = 'DavidLibre-Regular.ttf';

  const navigate = useNavigate();
  const { key } = useParams();

  // Use debounced window width hook (150ms debounce)
  const windowWidth = useWindowWidth(150);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    file.arrayBuffer();
    setSelectedFile(file);
  };

  // Update container bounds when window resizes
  useEffect(() => {
    setContainerBounds(containerRef.current?.getBoundingClientRect());
  }, [windowWidth]);


  useEffect(() => {
    const fetchNumPages = async () => {
      try {
        // Window width is now handled by useWindowWidth hook

        const pdf = await pdfjs.getDocument(pdfFile).promise;
        setNumPages(pdf.numPages);

        const firstPage = await pdf.getPage(1);
        const { width, height } = firstPage.getViewport({ scale: 1 });

        console.log([width, height]);
        setPageWidth(width);
        setPageHeight(height);

      } catch (error) {
        console.error('Error occurred while fetching PDF:', error);
      }
    };

    fetchNumPages();
  }, [pdfFile]);

  // useEffect(() => {
  //   // Function to fetch data from the backend server
  //   const fetchData = async () => {
  //     try {
  //       // Make the GET request using Axios
  //       const response = await axios.get(`${window.AppConfig.serverDomain}/api/organzations/document-input-fields/p12gq12t`);
  //       // const response = await axios.get(`http://localhost:3001/api/organzations/document-input-fields/${key}`);
  //       setInputFields(response.data.inputFields); // Update the state with the fetched data
  //       console.log(response.data.inputFields);
  //     } catch (error) {
  //       console.error('Error fetching data:', error);
  //     }
  //   };

  //   // Call the fetchData function when the component mounts
  //   fetchData();
  // }, []);

  useEffect(() => {
    const fetchNumPages = async () => {
      try {
        const existingPdfBytes = await selectedFile.arrayBuffer();
        const pdfDoc = await PDFLibDocument.load(existingPdfBytes);
        setNumPages(pdfDoc.getPageCount());

        const page = pdfDoc.getPage(0);
        const { width, height } = page.getSize();

        console.log([width, height]);
        setPageWidth(width);
        setPageHeight(height);

      } catch (error) {
        console.error('Error occurred while fetching PDF:', error);
      }
    };

    fetchNumPages();
    // Window width now handled by useWindowWidth hook

  }, [selectedFile]);



  const handlePageClick = (event, inputType) => {

    const containerBounds = containerRef.current.getBoundingClientRect();
    const containerX = containerBounds.left;
    const containerY = containerBounds.top;

    setContainerBounds(containerBounds);

    const containerwidthX = containerBounds.right - containerBounds.left;
    const containerwidthY = containerBounds.bottom - containerBounds.top;

    // console.log([containerwidthX, containerwidthY]);
    const clickXtemp = ((event.clientX - containerX) / containerwidthX);
    const clickYtemp = ((event.clientY - containerY) / containerwidthY);

    setClickX(clickXtemp);
    setClickY(clickYtemp);

    if (inputType === 1) {
      const newInputField = {
        x: clickXtemp,
        y: clickYtemp,
        value: "",
        page: currentPage,
        isCursor: false,
        options: [],
        mandatory: false,
        editor: {
          state: false,
          width: 0.4,
          height: 1,
          font: 1,
          renderType: 'text',
          inputType: { label: t('createTemplate.otherFieldLabel'), value: 'other' + generateRandomString(8), isOther: true },
        }
      };

      setInputFields([...inputFields.filter(field => !field.isCursor), newInputField]);
      SetAddingTextInputField(false);

    } else if (inputType === 2) {

      const newExtraInputField = {
        x: clickXtemp,
        y: clickYtemp,
        value: "data:image/png;base64,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",
        page: currentPage,
        isCursor: false,
        editor: {
          state: false,
          width: 1,
          height: 1,
          font: 1,
          inputType: { label: t('createTemplate.signatureFieldLabel'), value: 'signature' },
          signatureIndex: getNextSignatureIndex(inputFields, extraInputFields),
        }
      };

      setExtraInputFields([...extraInputFields.filter(field => !field.isCursor), newExtraInputField]);

      SetAddingExtraTextInputField(false);
    }

    console.log(signatures);

  };

  const generateRandomString = (length) => [...Array(length)].map(() => (~~(Math.random() * 36)).toString(36)).join('');

  // AIF-54: Helper function to get input type configuration based on selected field type
  const getInputTypeForSelection = (fieldType) => {
    switch(fieldType) {
      case 'signature':
        return {
          label: t('createTemplate.signatureFieldLabel'),
          value: 'signature',
          defaultValue: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAVQAAADICAYAAAC3QRk5AAAAAXNSR0IArs4c6QAABmJJREFUeF7t1DENADAMBLEEQPnTrVQKvdEB8IMV3c7MGUeAAAEC3wIrqN+GBggQIPAEBNUjECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIELjoXCvGAGlIAAAAAAElFTkSuQmCC",
          width: 1,
          height: 1
        };
      case 'checkbox':
        return {
          label: t('createTemplate.checkboxFieldLabel'),
          value: 'checkbox',
          defaultValue: false,
          width: 0.05,
          height: 0.05
        };
      case 'date':
        return {
          label: t('createTemplate.dateFieldLabel'),
          value: 'dateOfBirth',
          defaultValue: '',
          width: 0.3,
          height: 1
        };
      case 'select':
        return {
          label: t('createTemplate.selectFieldLabel'),
          value: 'select',
          defaultValue: '',
          width: 0.4,
          height: 1
        };
      case 'text':
        return {
          label: t('createTemplate.textFieldLabel'),
          value: 'childName',
          defaultValue: '',
          width: 0.4,
          height: 1
        };
      default: // 'other'
        return {
          label: t('createTemplate.otherFieldLabel'),
          value: 'other' + generateRandomString(8),
          isOther: true,
          defaultValue: '',
          width: 0.4,
          height: 1
        };
    }
  };

  const handleMouseMove = (event, inputType) => {
    const containerBounds = containerRef.current.getBoundingClientRect();
    const containerX = containerBounds.left;
    const containerY = containerBounds.top;

    setContainerBounds(containerBounds);

    const containerwidthX = containerBounds.right - containerBounds.left;
    const containerwidthY = containerBounds.bottom - containerBounds.top;

    // console.log([containerwidthX, containerwidthY]);
    const clickXtemp = ((event.clientX - containerX) / containerwidthX);
    const clickYtemp = ((event.clientY - containerY) / containerwidthY);

    setClickX(clickXtemp);
    setClickY(clickYtemp);

    const newClick = { x: clickXtemp * pageWidth, y: clickYtemp * pageHeight };
    setClicks([...clicks, newClick]);

    if (inputType === 1) {
      // AIF-54: Get field configuration based on selected type
      const fieldConfig = getInputTypeForSelection(selectedFieldType);

      const newInputField = {
        x: clickXtemp,
        y: clickYtemp,
        value: fieldConfig.defaultValue,
        page: currentPage,
        isCursor: true,
        options: [],
        mandatory: false,
        editor: {
          state: false,
          width: fieldConfig.width,
          height: fieldConfig.height,
          font: 1,
          renderType: selectedFieldType === 'select' ? 'dropdown' : 'text',
          inputType: { label: fieldConfig.label, value: fieldConfig.value, isOther: fieldConfig.isOther },
        }
      };

      setInputFields([...inputFields.filter(field => !field.isCursor), newInputField]);

      console.log([window.scrollX, window.scrollY, inputFields.find(field => field.isCursor)]);
    } else if (inputType === 2) {
      const newExtraInputField = {
        x: clickXtemp,
        y: clickYtemp,
        value: "data:image/png;base64,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",
        page: currentPage,
        isCursor: false,
        editor: {
          state: false,
          width: 1,
          height: 1,
          font: 1,
          inputType: { label: t('createTemplate.signatureFieldLabel'), value: 'signature' },
          signatureIndex: getNextSignatureIndex(inputFields, extraInputFields),
        }
      };

      setExtraInputFields([...extraInputFields.filter(field => !field.isCursor), newExtraInputField]);

      console.log([window.scrollX, window.scrollY, inputFields.find(field => field.isCursor)]);
    }
  };

  // Drag and drop handlers for AI suggestions
  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (event) => {
    event.preventDefault();

    try {
      const data = JSON.parse(event.dataTransfer.getData('application/json'));

      if (data.type === 'ai-suggestion') {
        const suggestion = data.suggestion;

        // CRITICAL FIX: Use the matched blank line coordinates, NOT the mouse position!
        // The suggestion already has the correct x/y from the matching algorithm
        const pageW = pageWidth || 612;
        const pageH = pageHeight || 792;

        let normalizedX, normalizedY, fieldWidth;

        if (suggestion.matchedBlankLine) {
          // Use matched blank line coordinates (same as Accept button)
          normalizedX = suggestion.x / pageW;
          normalizedY = suggestion.y / pageH;
          fieldWidth = Math.max(0.3, Math.min(0.7, (suggestion.width || 200) / pageW));

          console.log(`[DROP] Using matched blank line coords:`, {
            pdf: { x: suggestion.x, y: suggestion.y, width: suggestion.width },
            normalized: { x: normalizedX, y: normalizedY, width: fieldWidth }
          });
        } else {
          // Fallback: Use mouse position for unmatched fields
          const containerBounds = containerRef.current.getBoundingClientRect();
          const containerX = containerBounds.left;
          const containerY = containerBounds.top;
          const containerwidthX = containerBounds.right - containerBounds.left;
          const containerwidthY = containerBounds.bottom - containerBounds.top;

          normalizedX = ((event.clientX - containerX) / containerwidthX);
          normalizedY = ((event.clientY - containerY) / containerwidthY);
          fieldWidth = 0.4;

          console.log(`[DROP] No blank line match, using mouse position:`, {
            mouse: { x: event.clientX, y: event.clientY },
            normalized: { x: normalizedX, y: normalizedY }
          });
        }

        // Create field with matched coordinates
        const newInputField = {
          x: normalizedX,
          y: normalizedY,
          value: "",
          page: currentPage,
          isCursor: false,
          options: [],
          mandatory: false,
          editor: {
            state: false,
            width: fieldWidth,
            height: 1,
            font: 1,
            renderType: 'text',
            inputType: {
              label: suggestion.fieldTypeLabel || suggestion.label,
              value: suggestion.fieldType
            },
          }
        };

        setInputFields([...inputFields.filter(field => !field.isCursor), newInputField]);
        setAcceptedSuggestions(prev => [...prev, data.index]);

        console.log(`✅ Dropped AI field "${suggestion.fieldTypeLabel || suggestion.fieldType}" at normalized (${normalizedX.toFixed(3)}, ${normalizedY.toFixed(3)})`);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  const handleInputChange = (event, index) => {
    console.log(inputFields[index].editor.inputType.label);

    const updatedInputFields = [...inputFields];

    // Handle checkbox changes differently - use checked property
    const isCheckbox = event.target.type === 'checkbox';
    const newValue = isCheckbox ? event.target.checked.toString() : event.target.value;

    inputFields.forEach((field, index2) => {
      if (field.editor.inputType.value === inputFields[index].editor.inputType.value) {
        updatedInputFields[index2].value = newValue;
      }
    })
    setInputFields(updatedInputFields);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setClickY(null); // Reset clickY when the page changes
  };

  const handleOpenPDF = async () => {

    if (!selectedFile) {
      console.log('Please select a PDF file.');
      return;
    }

    console.log(inputFields);
    try {
      // Load the existing PDF document
      var existingPdfBytes = await fetch(pdfFile).then(res => res.arrayBuffer());

      if (selectedFile) {
        // existingPdfBytes = await selectedFile.arrayBuffer();
      }

      const pdfDoc = await PDFLibDocument.load(existingPdfBytes);

      // Register the fontkit instance
      pdfDoc.registerFontkit(fontkit);

      // Load the custom font file
      const fontBytes = await fetch(fontFileUrl).then(res => res.arrayBuffer());

      // Embed the font in the PDF document
      const customFont = await pdfDoc.embedFont(fontBytes);

      // Add the input field values as text on each page
      const pages = pdfDoc.getPages();
      inputFields.forEach(async inputField => {
        const isSignatureField = inputField.editor.inputType.value === 'signature';

        if (!isSignatureField) {
          // Draw text for all non-signature fields
          const { value } = inputField;
          const { x, y } = inputField;
          pages.forEach((page, index) => {
            if (inputField.page === index + 1) {
              const pageHeight = page.getHeight();
              const sizeFont = 12;
              const adjustedY = pageHeight - y * (pageHeight) - sizeFont; // Adjust the y-coordinate
              page.drawText(value, { x: x * (pageWidth), y: adjustedY, font: customFont, size: sizeFont, color: rgb(0, 0, 0) });
            }
          });
        } else {
          // Draw signature image
          console.log(inputField.value);
          // Embed the image in the PDF document
          const image = await pdfDoc.embedPng(inputField.value);
          const pngDims = image.scale(0.27)
          const { x, y } = inputField;
          pages.forEach((page, index) => {
            if (inputField.page === index + 1) {
              const pageHeight = page.getHeight();
              const adjustedY = pageHeight - y * (pageHeight) - pngDims.height;
              page.drawImage(image, {
                x: x * (pageWidth), y: adjustedY,
                width: pngDims.width,
                height: pngDims.height
              })
            }
          });
        }
      });

      // Generate the modified PDF document
      const modifiedPdfBytes = await pdfDoc.save();

      // Create a Blob object from the PDF data
      const modifiedPdfBlob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });

      // Create a URL for the Blob object
      const modifiedPdfUrl = URL.createObjectURL(modifiedPdfBlob);

      // Open the modified PDF in a new window
      const pdfWindow = window.open(modifiedPdfUrl);
      pdfWindow.focus();
    } catch (error) {
      console.log('Error opening PDF:', error);
    }
  };

  // const renderPdfContent = () => {
  //   return (
  //     <PdfPage>
  //       {
  //         inputFields.map((field, index) => (
  //           clicks.map((click, index) => (
  //             <View key={index} style={[styles.textContainer, { top: click.y, left: click.x }]}>
  //               <Text>Your Text Field</Text>
  //             </View>
  //           ))
  //         ))
  //       }

  //     </PdfPage>
  //   );
  // };

  const handleAddingTextInputField = () => {
    SetAddingTextInputField(true);
  }

  const handleAddingExtraTextInputField = () => {
    SetAddingExtraTextInputField(true);
  }

  const handleAddingSignatureInputField = () => {
    console.log(addingSignatureInputField);
    SetAddingSignatureInputField(true);
  }

  const handleMouseEnter = (event, index) => {
    if (!signatureOpen) {
      setHoveredIndex(index);
    }
  };

  const handleMouseLeave = () => {
    if (!signatureOpen) {
      setHoveredIndex(null);
    }
  };

  const switchEditorState = (index) => {
    const fieldToEdit = inputFields[index];
    fieldToEdit.editor.state = !fieldToEdit.editor.state;
    setInputFields([...inputFields.filter((field, liveIndex) => liveIndex !== index), fieldToEdit]);
  }

  const deleteField = (index) => {
    setInputFields([...inputFields.filter((field, liveIndex) => liveIndex !== index)]);
  }

  const handleSliderChange = (event, newValue, index) => {
    console.log([index, newValue]);
    setInputWidth(newValue, index);
  };

  const setInputWidth = (newValue, index) => {
    // const fieldToEdit = inputFields[index];
    // fieldToEdit.editor.width = newValue / 100;
    // setInputFields([...inputFields.filter((field, liveIndex) => liveIndex !== index), fieldToEdit]);
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields]; // Copy the existing array
      updatedInputFields[index].editor.width = newValue / 100; // Update the desired item
      return updatedInputFields; // Set the updated array as the new state
    });

  };

  const handleDragStart = (event) => {
    event.preventDefault();
  };

  const handleDragMove = (event, index) => {
    const delta = event.movementY;

    if (delta > 0) {
      setInputWidth(inputFields[index].filter.width * 100 - 1, index);
    } else if (delta < 0) {
      setInputWidth(inputFields[index].filter.width * 100 + 1, index);
    }
  };

  const handleHeightSliderChange = (event, newValue, index) => {
    console.log([index, newValue]);
    setInputHeight(newValue, index);
  };

  const handleHeightDragStart = (event) => {
    event.preventDefault();
  };

  const setInputHeight = (newValue, index) => {
    // const fieldToEdit = inputFields[index];
    // fieldToEdit.editor.width = newValue / 100;
    // setInputFields([...inputFields.filter((field, liveIndex) => liveIndex !== index), fieldToEdit]);
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields]; // Copy the existing array
      updatedInputFields[index].editor.height = newValue / 100; // Update the desired item
      return updatedInputFields; // Set the updated array as the new state
    });

  };

  const handleHeightDragMove = (event, index) => {
    const delta = event.movementY;

    if (delta > 0) {
      setInputWidth(inputFields[index].filter.height * 100 - 1, index);
    } else if (delta < 0) {
      setInputWidth(inputFields[index].filter.height * 100 + 1, index);
    }
  };

  const handleDrag = (e, data, index) => {
    const x = data.x;
    const y = data.y;
    console.log([x, y])
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields]; // Copy the existing array

      updatedInputFields[index].x = (x / windowWidth); // Update the desired item
      // Fix: Add back the height offset before converting to normalized coordinates
      updatedInputFields[index].y = (y + (windowWidth * updatedInputFields[index].editor.height / 40)) / (windowWidth * ((pageHeight || 792) / (pageWidth || 612)));

      return updatedInputFields; // Set the updated array as the new state
    });
  };

  const handleIndexChange = (event, index, coordinateType) => {
    console.log(inputFields[index]);
    const newValue = event.target.value;
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields]; // Copy the existing array
      if (coordinateType === 'x') {
        updatedInputFields[index].x = ((newValue < windowWidth) ? newValue : windowWidth) / windowWidth; // Update the desired item
      } else {
        updatedInputFields[index].y = ((newValue < (windowWidth * ((pageHeight || 792) / (pageWidth || 612)))) ? newValue : (windowWidth * ((pageHeight || 792) / (pageWidth || 612)))) / (windowWidth * ((pageHeight || 792) / (pageWidth || 612)));
        console.log(`[DRAG SAVE] field="${updatedInputFields[index].editor?.inputType?.value}" raw_y=${newValue} windowWidth=${windowWidth} pageHeight=${pageHeight} pageWidth=${pageWidth} saved_y=${updatedInputFields[index].y}`);
      }

      return updatedInputFields; // Set the updated array as the new state
    });
  }

  const handleInputTypeChange = (event, value, index) => {
    if (!value) return;
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields]; // Copy the existing array
      updatedInputFields[index].editor.inputType = value;
      if (value.value === 'signature') {
        updatedInputFields[index].value = "data:image/png;base64,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";
      } else if (value.label === t('createTemplate.otherFieldLabel')) {
        updatedInputFields[index].editor.inputType = { ...value, value: 'other' + generateRandomString(8), isOther: true };
        updatedInputFields[index].value = "";
      } else {
        updatedInputFields[index].value = "";
      }
      console.log(updatedInputFields);
      return updatedInputFields; // Set the updated array as the new state
    });
  }

  function handleOtherInputTypeChange(event, index){
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      updatedInputFields[index] = {
        ...updatedInputFields[index],
        editor: {
          ...updatedInputFields[index].editor,
          inputType: {
            ...updatedInputFields[index].editor.inputType,
            label: event.target.value,
            value: event.target.value
          }
        }
      };
      console.log(updatedInputFields);
      return updatedInputFields;
    });
  }

  const handleRenderTypeChange = (index, newRenderType) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields];
      updatedInputFields[index] = {
        ...updatedInputFields[index],
        options: newRenderType === 'text' ? [] : updatedInputFields[index].options,
        editor: { ...updatedInputFields[index].editor, renderType: newRenderType }
      };
      return updatedInputFields;
    });
  };

  const handleAddOption = (event, index) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields]; // Copy the existing array
      updatedInputFields[index].options.push("");
      console.log(updatedInputFields[index].options);
      return updatedInputFields; // Set the updated array as the new state
    });
  }

  const handleOptionChange = (e, index, optionIndex) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields]; // Copy the existing array
      updatedInputFields[index].options[optionIndex] = e.target.value;
      console.log(updatedInputFields[index].options[optionIndex]);
      return updatedInputFields; // Set the updated array as the new state
    });
  }

  const handleMandatoryChange = (event, index) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields]; // Copy the existing array
      updatedInputFields[index].mandatory = !updatedInputFields[index].mandatory;
      console.log(updatedInputFields[index].mandatory);
      return updatedInputFields; // Set the updated array as the new state
    });
  }

  const handleSelectChange = (event, index) => {
    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields]; // Copy the existing array
      updatedInputFields[index].value = event.target.value;
      console.log(updatedInputFields[index].value);
      return updatedInputFields; // Set the updated array as the new state
    });
  }

  function updateSignature(index, url) {

    setInputFields(prevInputFields => {
      const updatedInputFields = [...prevInputFields]; // Copy the existing array
      inputFields.forEach((field, index2) => {
        if (field.editor.inputType.value === inputFields[index].editor.inputType.value) {
          updatedInputFields[index2].value = url;
        }
      })
      // console.log(updatedInputFields);
      return updatedInputFields; // Set the updated array as the new state
    });

  }

  // Helper: Find nearest blank line to a label
  const findNearestBlankLine = (suggestion, blankLines) => {
    if (!blankLines || blankLines.length === 0) return null;

    const labelY = suggestion.y;
    const labelX = suggestion.x;
    const isRTL = suggestion.dir === 'rtl';

    // TIGHTER TOLERANCE: Filter blank lines on the same line (within 5 points vertically)
    // This prevents matching to wrong lines when labels are stacked vertically
    const sameLine = blankLines.filter(blank =>
      Math.abs(blank.y - labelY) < 5
    );

    if (sameLine.length === 0) {
      // Fallback with wider tolerance if exact match fails
      const widerSameLine = blankLines.filter(blank =>
        Math.abs(blank.y - labelY) < 15
      );

      if (widerSameLine.length === 0) return null;

      // Use wider tolerance candidates
      const validBlanks = widerSameLine.filter(blank =>
        isRTL ? blank.x < labelX : blank.x > labelX
      );

      if (validBlanks.length === 0) return null;

      // Return closest match with distance-based scoring
      return validBlanks.reduce((closest, blank) => {
        const dist = Math.abs(blank.x - labelX) + Math.abs(blank.y - labelY) * 2; // Penalize Y difference more
        const closestDist = Math.abs(closest.x - labelX) + Math.abs(closest.y - labelY) * 2;
        return dist < closestDist ? blank : closest;
      });
    }

    // For RTL: blank line should be to the LEFT of label (lower x value)
    // For LTR: blank line should be to the RIGHT of label (higher x value)
    const validBlanks = sameLine.filter(blank =>
      isRTL ? blank.x < labelX : blank.x > labelX
    );

    if (validBlanks.length === 0) {
      // Fallback: find the closest one on the same line (ignore direction)
      return sameLine.reduce((closest, blank) => {
        const dist = Math.abs(blank.x - labelX);
        const closestDist = Math.abs(closest.x - labelX);
        return dist < closestDist ? blank : closest;
      });
    }

    // Return the closest valid blank line (prefer horizontal proximity)
    return validBlanks.reduce((closest, blank) => {
      const dist = Math.abs(blank.x - labelX);
      const closestDist = Math.abs(closest.x - labelX);
      return dist < closestDist ? blank : closest;
    });
  };

  // AI Field Detection Handler (AIF-7)
  const handleAnalyzeWithAI = async () => {
    if (!selectedFile) {
      setAiError(t('createTemplate.aiErrorSelectPdf'));
      return;
    }

    setAiAnalyzing(true);
    setAiError(null);
    setAiSuggestions([]);

    try {
      console.log('[AI] Starting analysis...');

      // Step 1: Extract text from PDF
      const textItems = await extractTextWithCoordinates(selectedFile);
      const lines = groupTextIntoLines(textItems);
      const labels = detectFieldLabels(lines);

      console.log('[AI] Extraction complete:', {
        textItems: textItems.length,
        lines: lines.length,
        labels: labels.length
      });

      // STEP 1.5: Detect underscore/blank line positions
      const underscores = textItems.filter(item =>
        item.text && (item.text.includes('_') || item.text.includes('__'))
      );

      console.log('[AI] Underscore detection:', {
        found: underscores.length,
        positions: underscores.map(u => ({ text: u.text, x: u.x, y: u.y, width: u.width }))
      });

      // Store underscore positions for matching with AI suggestions
      const blankLinePositions = underscores.map(u => ({
        x: u.x,
        y: u.y,
        width: u.width,
        height: u.height || 12,
        page: u.page || 1,
        dir: u.dir || 'ltr'
      }));

      // Store for visual display
      setDetectedBlankLines(blankLinePositions);

      // CALIBRATION: Add reference markers to help backend understand coordinate system
      // Add 4 corner markers + 1 center marker
      const calibrationMarkers = [
        { text: '[CAL_TL]', x: 50, y: 50, width: 50, height: 12, page: 1, dir: 'ltr', purpose: 'top-left' },
        { text: '[CAL_TR]', x: 562, y: 50, width: 50, height: 12, page: 1, dir: 'rtl', purpose: 'top-right' },
        { text: '[CAL_BL]', x: 50, y: 780, width: 50, height: 12, page: 1, dir: 'ltr', purpose: 'bottom-left' },
        { text: '[CAL_BR]', x: 562, y: 780, width: 50, height: 12, page: 1, dir: 'rtl', purpose: 'bottom-right' },
        { text: '[CAL_C]', x: 306, y: 396, width: 50, height: 12, page: 1, dir: 'ltr', purpose: 'center' }
      ];

      // Store calibration data for frontend reference
      setCalibrationData({
        markers: calibrationMarkers,
        pageWidth: pageWidth || 612,
        pageHeight: pageHeight || 792
      });

      // Add calibration markers to text items
      const textItemsWithCalibration = [...textItems, ...calibrationMarkers];

      console.log('[AI] Added calibration markers:', calibrationMarkers.length);

      // Check if PDF has text
      if (textItems.length === 0) {
        throw new Error('PDF זה אינו מכיל טקסט. נא להעלות PDF מבוסס טקסט (לא סרוק).');
      }

      // Step 2: Call AI for field detection (with calibration markers)
      const aiResult = await analyzePDFWithAI({
        textItems: textItemsWithCalibration,
        lines,
        labels,
        calibration: calibrationMarkers  // Tell backend about calibration
      });

      console.log('[AI] AI analysis complete:', aiResult);

      // STEP 3: Match AI suggestions with blank line positions
      // IMPORTANT: Each blank line should only be matched once to prevent overlapping fields
      const usedBlankLines = new Set(); // Track which blank lines have been matched

      const suggestionsWithBlankLines = (aiResult.suggestions || []).map(suggestion => {
        // Find the nearest blank line to this label (excluding already-used ones)
        const availableBlankLines = blankLinePositions.filter(blank => {
          const key = `${blank.x}_${blank.y}`; // Unique key for this blank line
          return !usedBlankLines.has(key);
        });

        const nearestBlankLine = findNearestBlankLine(suggestion, availableBlankLines);

        if (nearestBlankLine) {
          // Mark this blank line as used
          const blankKey = `${nearestBlankLine.x}_${nearestBlankLine.y}`;
          usedBlankLines.add(blankKey);

          console.log(`[AI] Matched "${suggestion.label}" with blank line at x=${nearestBlankLine.x}, y=${nearestBlankLine.y}`);
          return {
            ...suggestion,
            originalX: suggestion.x,  // Keep original label position for reference
            originalY: suggestion.y,
            x: nearestBlankLine.x,    // Use blank line position
            y: nearestBlankLine.y,
            width: nearestBlankLine.width,
            matchedBlankLine: true
          };
        } else {
          console.warn(`[AI] No blank line found for "${suggestion.label}" (${usedBlankLines.size}/${blankLinePositions.length} blank lines already used)`);
          return {
            ...suggestion,
            matchedBlankLine: false
          };
        }
      });

      console.log('[AI] Suggestions with blank line matching:', suggestionsWithBlankLines.length);

      setAiSuggestions(suggestionsWithBlankLines);
      setShowAiSuggestions(true);

      // Auto-accept high-confidence suggestions (≥85%)
      const highConfidenceSuggestions = suggestionsWithBlankLines.filter(s => s.confidence >= 0.85);
      console.log(`[AI] Auto-placing ${highConfidenceSuggestions.length} high-confidence fields (≥85%)`);

      highConfidenceSuggestions.forEach((suggestion, idx) => {
        const originalIndex = suggestionsWithBlankLines.indexOf(suggestion);
        handleAcceptSuggestion(suggestion, originalIndex);
      });

      setAiAnalyzing(false);

    } catch (error) {
      console.error('[AI] Error:', error);
      setAiError(error.message || t('createTemplate.aiErrorGeneral'));
      setAiAnalyzing(false);
    }
  };

  const handleAcceptSuggestion = (suggestion, index) => {
    console.log('[AI] Accepting suggestion:', suggestion);
    console.log('[AI] Page dimensions:', { pageWidth, pageHeight });

    // CRITICAL FIX: If we matched a blank line, suggestion.x/y are already the blank line coordinates!
    // Just normalize them to 0-1 range like the blue markers do

    const pageW = pageWidth || 612;
    const pageH = pageHeight || 792;

    let normalizedX;
    let normalizedY;
    let fieldWidth;

    if (suggestion.matchedBlankLine) {
      // Use the matched blank line coordinates (already corrected by matching algorithm)
      normalizedX = suggestion.x / pageW;
      normalizedY = suggestion.y / pageH;
      fieldWidth = Math.max(0.3, Math.min(0.7, (suggestion.width || 200) / pageW));

      // Calculate what the display position will be (for debugging)
      const displayX = normalizedX * windowWidth;
      const displayY = normalizedY * windowWidth * ((pageHeight || 792) / (pageWidth || 612));
      const blankDisplayY = suggestion.y / pageH * windowWidth * ((pageHeight || 792) / (pageWidth || 612));

      // Check actual PDF canvas width
      const pdfCanvas = document.querySelector('.react-pdf__Page__canvas');
      const actualPDFWidth = pdfCanvas ? pdfCanvas.parentElement.offsetWidth : 'not found';

      console.log('[AI] Using matched blank line coordinates:', {
        pdf_coords: { x: suggestion.x, y: suggestion.y, width: suggestion.width },
        page_dims: { width: pageW, height: pageH },
        normalized: { x: normalizedX, y: normalizedY, width: fieldWidth },
        display_pixels: { x: displayX, y: displayY },
        blank_display_y: blankDisplayY,
        windowWidth_state: windowWidth,
        actualPDFWidth_DOM: actualPDFWidth,
        percentages: {
          x: `${(normalizedX * 100).toFixed(1)}%`,
          y: `${(normalizedY * 100).toFixed(1)}%`,
          width: `${(fieldWidth * 100).toFixed(1)}%`
        }
      });
    } else {
      // Fallback: No blank line matched, use original positioning logic
      normalizedY = suggestion.y / pageH;

      // Check if this is a checkbox field - use backend coordinates directly
      // Match based on label or field type containing checkbox-related keywords
      const isCheckboxField =
        // English checkbox field values
        suggestion.fieldTypeValue === 'receivedFullVaccination' ||
        suggestion.fieldTypeValue === 'allowsPhotographingInternal' ||
        suggestion.fieldTypeValue === 'allowsPhotographingExternal' ||
        // Hebrew checkbox field type values (full translations)
        suggestion.fieldTypeValue === 'האם ההורים מאשרים לצילום הילד פנימי' ||
        suggestion.fieldTypeValue === 'האם ההורים מאשרים לצילום הילד חוץ' ||
        suggestion.fieldTypeValue === 'אלרגיה לתרופות' ||
        suggestion.fieldTypeValue === 'סכום תשלום חודשה' ||
        // Hebrew checkbox field labels
        suggestion.fieldTypeLabel === 'האם ההורים מאשרים לצילום הילד פנימי' ||
        suggestion.fieldTypeLabel === 'האם ההורים מאשרים לצילום הילד חוץ' ||
        suggestion.fieldTypeLabel === 'אלרגיה לתרופות' ||
        suggestion.fieldTypeLabel === 'סכום תשלום חודשה' ||
        // Short labels from PDF (detected by backend) - all 3 checkbox labels
        suggestion.label === 'צילום' ||
        suggestion.label === 'מתן' ||
        suggestion.label === 'תשלום' ||
        // Keywords in fieldType or label
        suggestion.fieldTypeLabel?.includes('מאשרים') ||
        suggestion.fieldTypeLabel?.includes('אלרגיה') ||
        suggestion.fieldType?.includes('מאשרים') ||
        suggestion.fieldType?.includes('אלרגיה');

      if (isCheckboxField) {
        // For checkboxes in RTL documents:
        // Backend detects LABEL position, but checkbox is to the LEFT of the label
        // We need to position the checkbox to the left of the detected label position

        if (suggestion.dir === 'rtl') {
          // RTL: Checkbox is to the LEFT of the label
          // Position checkbox at: label_x - label_width - small_gap
          const checkboxOffset = 25; // Pixels to the left of the label
          normalizedX = (suggestion.x - suggestion.width - checkboxOffset) / pageW;
        } else {
          // LTR: Checkbox might be to the right (less common)
          normalizedX = suggestion.x / pageW;
        }

        fieldWidth = Math.max(0.02, Math.min(0.05, 15 / pageW)); // Fixed small width for checkboxes
        console.log('[AI] Checkbox field - adjusted position:', {
          original_x: suggestion.x,
          label_width: suggestion.width,
          adjusted_x: normalizedX * pageW,
          dir: suggestion.dir
        });
      } else if (suggestion.dir === 'rtl') {
        const blankLineStart = 100;
        const blankLineEnd = suggestion.x - suggestion.width - 30;
        const fieldWidthPx = blankLineEnd - blankLineStart;
        fieldWidth = Math.max(0.4, fieldWidthPx / pageW);
        normalizedX = blankLineStart / pageW;
      } else {
        normalizedX = suggestion.x / pageW;
        fieldWidth = Math.max(0.3, Math.min(0.7, (suggestion.width || 300) / pageW));
      }

      console.log('[AI] No blank line match, using fallback positioning');
    }

    console.log('[AI] Field positioning:', {
      suggestion_x: suggestion.x,
      suggestion_y: suggestion.y,
      suggestion_width: suggestion.width,
      dir: suggestion.dir,
      page_size: { width: pageWidth || 595, height: pageHeight || 842 },
      normalized: { x: normalizedX, y: normalizedY, width: fieldWidth },
      percentages: {
        x: `${(normalizedX * 100).toFixed(1)}%`,
        y: `${(normalizedY * 100).toFixed(1)}%`,
        width: `${(fieldWidth * 100).toFixed(1)}%`
      }
    });

    // Use fieldTypeValue (English) for input type value, fieldTypeLabel (Hebrew) for display
    // This ensures signature1/signature2 logic works correctly
    const inputTypeValue = suggestion.fieldTypeValue || suggestion.fieldType;
    const inputTypeLabel = suggestion.fieldTypeLabel || suggestion.label;

    // Set default value based on field type
    let defaultValue = '';
    // Check if this is a checkbox field (English or Hebrew)
    const isCheckboxField = inputTypeValue === 'receivedFullVaccination' ||
        inputTypeValue === 'allowsPhotographingInternal' ||
        inputTypeValue === 'allowsPhotographingExternal' ||
        inputTypeValue === 'צילום' ||
        inputTypeValue === 'מתן תרופות' ||
        inputTypeValue === 'תשלום עבור איחור' ||
        inputTypeLabel?.includes('צילום') ||
        inputTypeLabel?.includes('תרופות') ||
        inputTypeLabel?.includes('איחור');

    if (isCheckboxField) {
      defaultValue = 'false';  // Checkboxes default to unchecked
    }

    const newField = {
      x: normalizedX,  // Now correctly adjusted for RTL
      y: normalizedY,
      page: suggestion.page || 1,
      value: defaultValue,
      isCursor: false,
      options: [],  // Required for select fields
      mandatory: false,
      dir: suggestion.dir || 'ltr',  // FIXED: Save text direction
      editor: {
        inputType: {
          value: inputTypeValue,  // CRITICAL: Use English value for logic (signature1, signature2)
          label: inputTypeLabel   // Use Hebrew label for display (חתימה 1, חתימה 2)
        },
        state: false,
        width: fieldWidth,
        height: 0.7,
        font: 1,
        renderType: 'text',
      }
    };

    console.log('[AI] Converted field:', {
      original: { x: suggestion.x, y: suggestion.y, width: suggestion.width, dir: suggestion.dir },
      converted: { x: newField.x, y: newField.y, width: newField.editor.width, dir: newField.dir },
      percentages: {
        x: `${(newField.x * 100).toFixed(1)}%`,
        y: `${(newField.y * 100).toFixed(1)}%`,
        width: `${(newField.editor.width * 100).toFixed(1)}%`
      }
    });

    // Add to inputFields
    setInputFields(prev => {
      const updated = [...prev, newField];
      console.log('[AI] Field added to form. Total fields:', updated.length);
      console.log('[AI] New field Y coordinate:', {
        normalized_y: newField.y,
        should_display_at_px: newField.y * windowWidth * ((pageHeight || 792) / (pageWidth || 612)),
        pdf_y_coordinate: suggestion.y
      });
      return updated;
    });
    setAcceptedSuggestions(prev => [...prev, index]);

    console.log('[AI] Field added successfully');
  };

  // Handle rejecting AI suggestion
  const handleRejectSuggestion = (suggestion, index) => {
    setRejectedSuggestions(prev => [...prev, index]);
  };

  // Handle editing AI suggestion
  const handleEditSuggestion = (suggestion, index) => {
    // For now, just accept it - user can edit manually after
    handleAcceptSuggestion(suggestion, index);
  };

  // Handle accept all suggestions
  const handleAcceptAllSuggestions = (suggestions) => {
    suggestions.forEach((suggestion, index) => {
      if (!acceptedSuggestions.includes(index) && !rejectedSuggestions.includes(index)) {
        handleAcceptSuggestion(suggestion, index);
      }
    });
  };

  // Handle reject all suggestions
  const handleRejectAllSuggestions = (suggestions) => {
    const allIndices = suggestions.map((_, index) => index);
    setRejectedSuggestions(allIndices);
    setAcceptedSuggestions([]);
  };

  async function saveInputFields() {
    setLoading(true);
    await handleSubmit();
    setLoading(false);
  }

  const handleSubmit = async () => {
    // const localUrl = "http://localhost:3001/api/organzations/create-template/";
    const localUrl = `${window.AppConfig.serverDomain}/api/organzations/create-template/`;

    // const staticInputFields = inputFields.filter(obj => !obj.editor.inputType.value.includes("other"));
    // console.log(staticInputFields);

    // const extraInputFields = inputFields.filter(obj => obj.editor.inputType.value.includes("other"));
    // console.log(extraInputFields);

    const data = new FormData();
    data.append('pdf', selectedFile);
    data.append('data', JSON.stringify(inputFields));
    // data.append('extraData', JSON.stringify(extraInputFields));
    data.append('templateName', selectedFile.name);
    data.append('orgID', key);
    data.append('requireID', requireID);
    data.append('requestedFiles', JSON.stringify(requestedFiles));
    // Add more fields as needed

    try {
      const response = await axios.post(localUrl, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log(`/${response.data.documentURL.split('/').pop().split('.')[0]}`);
      console.log(`${currentProtocol}//${currentDomain}/template/${response.data.documentURL.split('/').pop().split('.')[0].split('_').pop()}`);

      setTemplateLink(`${currentProtocol}//${currentDomain}/template/${response.data.documentURL.split('/').pop().split('.')[0].split('_').pop()}`);

      // console.log(`/${response.data.documentURL.split('/').pop().split('.')[0]}`);
      // console.log(`${currentProtocol}//${currentDomain}:3000/template/${response.data.documentURL.split('/').pop().split('.')[0]}`);

      // setTemplateLink(`${currentProtocol}//${currentDomain}:3000/template/${response.data.documentURL.split('/').pop().split('.')[0]}`);

      setLinkFetched(true);
      // Do something with the response if needed
    } catch (error) {
      console.error('Error:', error);
    }
  };

  function handlePdfLoadSuccess(page) {
    // Window width now handled by useWindowWidth hook

    // Get page dimensions from the loaded page
    if (page && page.originalWidth && page.originalHeight) {
      const pdfPageWidth = page.originalWidth;
      const pdfPageHeight = page.originalHeight;

      setPageWidth(pdfPageWidth);
      setPageHeight(pdfPageHeight);

      // Set calibration markers immediately when PDF loads
      const calibrationMarkers = [
        { text: '[CAL_TL]', x: 50, y: 50, width: 50, height: 12, page: 1, dir: 'ltr', purpose: 'top-left' },
        { text: '[CAL_TR]', x: pdfPageWidth - 50, y: 50, width: 50, height: 12, page: 1, dir: 'rtl', purpose: 'top-right' },
        { text: '[CAL_BL]', x: 50, y: pdfPageHeight - 50, width: 50, height: 12, page: 1, dir: 'ltr', purpose: 'bottom-left' },
        { text: '[CAL_BR]', x: pdfPageWidth - 50, y: pdfPageHeight - 50, width: 50, height: 12, page: 1, dir: 'rtl', purpose: 'bottom-right' },
        { text: '[CAL_C]', x: pdfPageWidth / 2, y: pdfPageHeight / 2, width: 50, height: 12, page: 1, dir: 'ltr', purpose: 'center' }
      ];

      setCalibrationData({
        markers: calibrationMarkers,
        pageWidth: pdfPageWidth,
        pageHeight: pdfPageHeight
      });

      console.log('[Calibration] PDF loaded, markers set:', {
        pageWidth: pdfPageWidth,
        pageHeight: pdfPageHeight,
        markers: calibrationMarkers.length
      });
    }
  }

  const handleCheckboxChange = (event) => {
    setRequireID(event.target.checked);
    console.log(event.target.checked);
  };

  // Use the documentBytes as needed, e.g., display the PDF
  return (
    <>
      {/* AI Suggestions Compact Drawer */}
      {aiSuggestions.length > 0 && (
        <AISuggestedFieldsCompact
          suggestions={aiSuggestions}
          onAccept={handleAcceptSuggestion}
          onReject={handleRejectSuggestion}
          onAcceptAll={handleAcceptAllSuggestions}
          onRejectAll={handleRejectAllSuggestions}
          acceptedFields={acceptedSuggestions}
          rejectedFields={rejectedSuggestions}
          open={showAiSuggestions}
          onClose={() => setShowAiSuggestions(false)}
        />
      )}

      <Box sx={{ padding: '20px 24px', textAlign: 'start' }}>
        <Typography variant="h4" sx={{ fontWeight: '700', color: colors.primary.main, marginBottom: 3 }}>
          {t('createTemplate.pageTitle')}
        </Typography>

        {!selectedFile ? (
          <Box sx={{
            border: `2px dashed ${colors.secondary.main}`,
            borderRadius: '16px',
            padding: '48px 24px',
            textAlign: 'center',
            backgroundColor: '#f8f9ff',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <UploadFileIcon sx={{ fontSize: '64px', color: colors.secondary.main, marginBottom: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: '600', color: colors.primary.main, marginBottom: 2 }}>
              {t('createTemplate.uploadTitle')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#495057', marginBottom: 3 }}>
              {t('createTemplate.uploadSubtitle')}
            </Typography>

            <Box sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: 3,
              flexWrap: 'wrap'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  backgroundColor: colors.primary.main,
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>1</Box>
                <Typography variant="body2" sx={{ color: '#495057' }}>{t('createTemplate.step1')}</Typography>
              </Box>
              <ArrowForwardIcon sx={{ color: colors.secondary.main }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  backgroundColor: colors.primary.main,
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>2</Box>
                <Typography variant="body2" sx={{ color: '#495057' }}>{t('createTemplate.step2')}</Typography>
              </Box>
              <ArrowForwardIcon sx={{ color: colors.secondary.main }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  backgroundColor: colors.primary.main,
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>3</Box>
                <Typography variant="body2" sx={{ color: '#495057' }}>{t('createTemplate.step3')}</Typography>
              </Box>
            </Box>

            <input
              id="formFile"
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              aria-label={t('createTemplate.selectPdfAriaLabel')}
              style={{ display: 'none' }}
            />
            <label htmlFor="formFile">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadFileIcon />}
                sx={{
                  backgroundColor: colors.primary.main,
                  color: 'white',
                  fontWeight: '600',
                  padding: '12px 32px',
                  fontSize: '16px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'colors.primary.dark'
                  }
                }}
              >
                {t('createTemplate.uploadButton')}
              </Button>
            </label>
          </Box>
        ) : null}
      </Box>
      {selectedFile && <>
        {/* Tabbed Interface */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            aria-label="Template editor tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '16px',
                minWidth: 120,
              },
              '& .Mui-selected': {
                color: colors.primary.main,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: colors.primary.main,
              }
            }}
          >
            <Tab label={t('createTemplate.tabs.pdfEditor')} id="template-tab-0" aria-controls="template-tabpanel-0" />
            <Tab label={t('createTemplate.tabs.requiredDocuments')} id="template-tab-1" aria-controls="template-tabpanel-1" />
          </Tabs>
        </Box>

        {/* Tab Panel 0: PDF Editor */}
        <TabPanel value={activeTab} index={0}>
        {/* Help Text for PDF Editor Tab */}
        <Box sx={{ marginBottom: 3, padding: 2, backgroundColor: '#e3f2fd', borderRadius: 2, borderLeft: '4px solid #2196f3' }}>
          <Typography variant="body2" sx={{ color: '#1565c0', lineHeight: 1.6 }}>
            💡 <strong>{t('createTemplate.tips.pdfEditorTitle')}</strong> {t('createTemplate.tips.pdfEditorText')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginBottom: 3, alignItems: 'center' }} role="toolbar" aria-label={t('createTemplate.toolbarAriaLabel')}>
          {/* AIF-54: Field Type Selector Dropdown */}
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel id="field-type-select-label">{t('createTemplate.fieldTypeLabel')}</InputLabel>
            <Select
              labelId="field-type-select-label"
              id="field-type-select"
              value={selectedFieldType}
              label={t('createTemplate.fieldTypeLabel')}
              onChange={(e) => setSelectedFieldType(e.target.value)}
              sx={{
                backgroundColor: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.primary.main,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.primary.dark,
                },
              }}
            >
              <MenuItem value="text">📝 {t('createTemplate.fieldTypes.text')}</MenuItem>
              <MenuItem value="signature">✍️ {t('createTemplate.fieldTypes.signature')}</MenuItem>
              <MenuItem value="checkbox">☑️ {t('createTemplate.fieldTypes.checkbox')}</MenuItem>
              <MenuItem value="date">📅 {t('createTemplate.fieldTypes.date')}</MenuItem>
              <MenuItem value="select">▼ {t('createTemplate.fieldTypes.select')}</MenuItem>
              <MenuItem value="other">📄 {t('createTemplate.fieldTypes.other')}</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleAddingTextInputField}
            startIcon={<AddIcon />}
            aria-label={t('createTemplate.addFieldAriaLabel')}
            sx={{
              backgroundColor: colors.primary.main,
              color: 'white',
              fontWeight: '600',
              padding: '10px 20px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'colors.primary.dark'
              }
            }}
          >
            {t('createTemplate.addFieldButton')}
          </Button>

          {/* AI Analysis Button (AIF-7) */}
          <Button
            variant="outlined"
            onClick={handleAnalyzeWithAI}
            disabled={aiAnalyzing}
            startIcon={aiAnalyzing ? null : <AutoAwesomeIcon />}
            aria-label={aiAnalyzing ? t('createTemplate.analyzingAIAriaLabel') : t('createTemplate.analyzeWithAIAriaLabel')}
            aria-busy={aiAnalyzing}
            sx={{
              borderColor: colors.secondary.main,
              color: colors.primary.main,
              fontWeight: '500',
              padding: '10px 20px',
              textTransform: 'none',
              '&:hover': {
                borderColor: colors.primary.main,
                backgroundColor: colors.state.hover
              }
            }}
          >
            {aiAnalyzing ? (
              <>
                <AIAnalysisSpinner />
                {t('createTemplate.analyzingWithAI')}
              </>
            ) : (
              t('createTemplate.analyzeWithAI')
            )}
          </Button>
        </Box>

        {/* AI Error Message */}
        {aiError && (
          <Box sx={{ padding: 2, marginBottom: 2, bgcolor: '#ffebee', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {aiError}
            </Typography>
          </Box>
        )}

        {/* DEBUG Control Panel - COMMENTED OUT TO TEST IF IT CAUSES OFFSET */}
        {/* {aiSuggestions.length > 0 && (
          <Box sx={{ padding: 2, marginBottom: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '2px solid #ff9800' }}>
            <Typography sx={{ fontWeight: 'bold', marginBottom: 1 }}>
              🔍 DEBUG MODE - AI Coordinate Markers
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              Red/Yellow circles show where backend AI sent coordinates (x={aiSuggestions[0]?.x}, y={aiSuggestions[0]?.y})
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              👉 Do these markers appear on the LABEL or on the BLANK LINE?
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={() => setShowDebugMarkers(!showDebugMarkers)}
              sx={{ marginRight: 1 }}
            >
              {showDebugMarkers ? 'Hide Markers' : 'Show Markers'}
            </Button>
            <Typography variant="caption" sx={{ display: 'block', marginTop: 1, color: '#666' }}>
              Page dimensions: {pageWidth || 595} × {pageHeight || 842} points
            </Typography>
          </Box>
        )} */}

      <div
        ref={containerRef}
        style={{ width: '100%', overflow: 'visible', position: 'relative' }}
        onMouseMove={addingTextInputField ? (event) => handleMouseMove(event, 1) : addingExtraTextInputField ? (event) => handleMouseMove(event, 2) : null}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <Document file={selectedFile}  >
          {/* {Array.from(new Array(numPages), (el, index) => (
            <Page key={index} pageNumber={index + 1} onClick={handlePageClick} width={containerRef.current?.clientWidth} />
          ))} */}
          <div style={{ pointerEvents: `${addingTextInputField || addingSignatureInputField ? 'auto' : 'none'}` }} >

            <Page onLoadSuccess={handlePdfLoadSuccess} key={currentPage - 1} pageNumber={currentPage} onClick={addingTextInputField ? (event) => handlePageClick(event, 1) : addingExtraTextInputField ? (event) => handlePageClick(event, 2) : null} width={containerRef.current?.clientWidth} />


          </div>
        </Document>

        {inputFields.map((inputField, index) => (
          (inputField.page === currentPage) ?
            <React.Fragment key={index}>
              {
                inputField.editor.inputType.value === 'signature' ? (
                  <div key={index}
                    style={{
                      opacity: `${hoveredIndex === index || inputField.editor.state ? 0.8 : 1}`,
                      position: 'absolute',
                      top: inputField.y * (windowWidth) * ((pageHeight || 792) / (pageWidth || 612)),
                      left: inputField.x * (windowWidth),
                      fontSize: `${windowWidth / 40}px`,
                      width: `${(windowWidth * inputField.editor.width / 1.5)}px`,
                      padding: '4px',
                      ...(getFieldTypeStyle(inputField.editor.inputType) || {}),
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(event) => handleMouseEnter(event, index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {/* Field Type Icon Badge for Signature */}
                    {getFieldTypeStyle(inputField.editor.inputType) && (
                      <span style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '-10px',
                        fontSize: '18px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        border: `2px solid ${getFieldTypeStyle(inputField.editor.inputType).color}`,
                        zIndex: 11,
                        pointerEvents: 'none',
                      }}>
                        {getFieldTypeStyle(inputField.editor.inputType).icon}
                      </span>
                    )}

                    <SignatureModal key={index} windowWidth={windowWidth} setHoveredIndex={setHoveredIndex} setSignatureOpen={setSignatureOpen} updateSignature={updateSignature} url={inputField.value} index={index} signer={t('createTemplate.signatureLabel')} />

                  </div>) : (<Draggable handle={inputField.editor.state ? ".field-drag-handle, .edit-card-drag-handle" : ".field-drag-handle"} onDrag={(e, data) => handleDrag(e, data, index)} position={{ x: inputField.x * (windowWidth), y: inputField.y * (windowWidth) * ((pageHeight || 792) / (pageWidth || 612)) - (windowWidth * inputField.editor.height / 40) }} >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      ...(getFieldTypeStyle(inputField.editor.inputType) || {}),
                      borderRadius: '4px',
                      padding: '2px',
                      transition: 'all 0.2s ease',
                    }}>
                      {/* Field Type Icon Badge */}
                      {getFieldTypeStyle(inputField.editor.inputType) && (
                        <span style={{
                          position: 'absolute',
                          top: '-10px',
                          right: '-10px',
                          fontSize: '18px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          border: `2px solid ${getFieldTypeStyle(inputField.editor.inputType).color}`,
                          zIndex: 11,
                          pointerEvents: 'none',
                        }}>
                          {getFieldTypeStyle(inputField.editor.inputType).icon}
                        </span>
                      )}

                      {/* Drag Handle Bar */}
                      <div className="field-drag-handle" style={{
                        position: 'absolute',
                        top: '-20px',
                        left: 0,
                        right: 0,
                        height: '18px',
                        background: colors.primary.main,
                        cursor: 'move',
                        borderRadius: '4px 4px 0 0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        color: 'white',
                        fontWeight: '600',
                        opacity: hoveredIndex === index || inputField.editor.state ? 1 : 0,
                        transition: 'opacity 0.2s',
                        zIndex: 10
                      }}>
                        ⋮⋮
                      </div>

                      {/* Checkbox rendering for checkbox field types */}
                      {/* Uses isCheckboxField() helper defined at top of file */}
                      {isCheckboxField(inputField.editor.inputType) ? (
                        <input
                          key={index}
                          type="checkbox"
                          checked={inputField.value === 'true' || inputField.value === true}
                          onChange={(event) => handleInputChange(event, index)}
                          style={{
                            opacity: `${hoveredIndex === index || inputField.editor.state ? 0.8 : 1}`,
                            width: `${windowWidth * inputField.editor.height / 40}px`,
                            height: `${windowWidth * inputField.editor.height / 40}px`,
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(event) => handleMouseEnter(event, index)}
                          onMouseLeave={handleMouseLeave}
                        />
                      ) : inputField?.options?.length === 0 ? <input
                        key={index}
                        type="text"
                        value={inputField.value}
                        onChange={(event) => handleInputChange(event, index)}
                        style={{
                          opacity: `${hoveredIndex === index || inputField.editor.state ? 0.8 : 1}`,
                          width: `${(windowWidth * inputField.editor.width / 1.5)}px`,
                          height: `${windowWidth * inputField.editor.height / 40}px`,
                          fontSize: `${windowWidth / 60}px`,
                          padding: '4px',
                        }}
                        onMouseEnter={(event) => handleMouseEnter(event, index)}
                        onMouseLeave={handleMouseLeave}
                      /> :
                        <Select
                          style={{
                            opacity: `${hoveredIndex === index || inputField.editor.state ? 0.8 : 1}`,
                            width: `${(windowWidth * inputField.editor.width / 1.5)}px`,
                            height: `${windowWidth * inputField.editor.height / 40}px`,
                            fontSize: `${windowWidth / 60}px`,
                            padding: '4px',
                          }}
                          value={inputField.value}
                          onChange={(event) => handleSelectChange(event, index)}
                          displayEmpty
                          fullWidth
                          onMouseEnter={(event) => handleMouseEnter(event, index)}
                          onMouseLeave={handleMouseLeave}
                        >
                          <MenuItem value=""  >
                            -- בחר אופציה --
                          </MenuItem>
                          {inputField.options.map((input, inputIndex) => (
                            <MenuItem key={inputIndex} value={input}>
                              {input || t('createTemplate.selectOptionPlaceholder')} {/* Display 'Empty Input' if the input is an empty string */}
                            </MenuItem>
                          ))}
                        </Select>}


                      {/* Edit Icon */}
                      {hoveredIndex === index && (
                        <FaEdit
                          onMouseEnter={(event) => handleMouseEnter(event, index)}
                          onMouseLeave={handleMouseLeave}
                          onClick={() => switchEditorState(index)}
                          style={{
                            height: `${windowWidth / 40}px`,
                            marginLeft: `${windowWidth / 200}px`,
                            cursor: 'pointer',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 20
                          }}
                        />
                      )}

                      {/* Edit Card */}
                      {inputField.editor.state && (
                        <div style={{
                          position: 'absolute',
                          top: `${windowWidth / 40}px`,
                          left: 0,
                          width: `${windowWidth > 700 ? '320px' : '280px'}`,
                          maxWidth: '90vw',
                          zIndex: 1000,
                          backgroundColor: '#ffffff',
                          borderRadius: '16px',
                          boxShadow: '0 8px 32px rgba(19, 36, 161, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
                          border: '1px solid rgba(19, 36, 161, 0.1)',
                          overflow: 'hidden',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                        }}>
                  {/* Header - Drag Handle */}
                  <div className="edit-card-drag-handle" style={{
                    background: colors.primary.main,
                    padding: '16px 20px',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '600',
                    letterSpacing: '0.3px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'move'
                  }}>
                    {t('createTemplate.fieldSettings')}
                  </div>

                  {/* Content */}
                  <div style={{
                    padding: '20px',
                    backgroundColor: '#ffffff',
                    maxHeight: '70vh',
                    overflowY: 'auto'
                  }}>

                    {/* Field Type */}
                    <div style={{ marginBottom: '20px' }}>
                      <Autocomplete
                        disablePortal
                        options={options}
                        onChange={(event, value) => handleInputTypeChange(event, value, index)}
                        value={inputField.editor.inputType}
                        renderInput={(params) => <TextField {...params} label={t('createTemplate.fieldTypeLabel')} size="small" />}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa'
                          }
                        }}
                      />
                      {inputField.editor.inputType.isOther && (
                        <TextField
                          label={t('createTemplate.fieldNameLabel')}
                          variant="outlined"
                          size="small"
                          value={inputField.editor.inputType.value?.startsWith('other') ? '' : inputField.editor.inputType.value}
                          onChange={(event) => handleOtherInputTypeChange(event, index)}
                          fullWidth
                          sx={{
                            marginTop: '12px',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: '#f8f9fa'
                            }
                          }}
                        />
                      )}
                    </div>

                    {/* Render Type Toggle */}
                    {getFieldControlVisibility(inputField.editor.inputType, inputField.editor.renderType, inputField.options?.length).showRenderTypeToggle && (
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#495057',
                          marginBottom: '8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          סוג תצוגה
                        </div>
                        <ToggleButtonGroup
                          value={inputField.editor.renderType || (inputField.options?.length > 0 ? 'dropdown' : 'text')}
                          exclusive
                          onChange={(_, val) => val && handleRenderTypeChange(index, val)}
                          fullWidth
                          size="small"
                          sx={{ '& .MuiToggleButton-root': { borderRadius: '8px', fontWeight: '500' } }}
                        >
                          <ToggleButton value="text" sx={{ flex: 1 }}>טקסט</ToggleButton>
                          <ToggleButton value="dropdown" sx={{ flex: 1 }}>רשימה נפתחת</ToggleButton>
                        </ToggleButtonGroup>
                      </div>
                    )}

                    {/* Dimensions Section - Conditional (AIF-48) */}
                    {getFieldControlVisibility(inputField.editor.inputType, inputField.editor.renderType, inputField.options?.length).showDimensions && (
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#495057',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <AspectRatioIcon sx={{ fontSize: '16px' }} />
                        {t('createTemplate.dimensions')}
                      </div>

                      {/* Width Slider */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{ fontSize: '13px', color: '#6c757d', fontWeight: '500' }}>{t('createTemplate.width')}</span>
                          <span style={{
                            backgroundColor: colors.primary.main,
                            color: 'white',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            minWidth: '45px',
                            textAlign: 'center'
                          }}>
                            {Math.trunc(inputField.editor.width * 100)}%
                          </span>
                        </div>
                        <Slider
                          value={inputField.editor.width * 100}
                          min={5}
                          max={100}
                          step={1}
                          onChange={(event, newValue) => handleSliderChange(event, newValue, index)}
                          sx={{
                            color: colors.primary.main,
                            height: 6,
                            '& .MuiSlider-thumb': {
                              width: 20,
                              height: 20,
                              backgroundColor: '#fff',
                              border: '3px solid currentColor',
                              '&:hover': {
                                boxShadow: '0 0 0 8px rgba(19, 36, 161, 0.16)'
                              }
                            },
                            '& .MuiSlider-track': {
                              border: 'none',
                              height: 6
                            },
                            '& .MuiSlider-rail': {
                              opacity: 0.3,
                              height: 6
                            }
                          }}
                        />
                      </div>

                      {/* Height Slider */}
                      <div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{ fontSize: '13px', color: '#6c757d', fontWeight: '500' }}>{t('createTemplate.height')}</span>
                          <span style={{
                            backgroundColor: colors.primary.main,
                            color: 'white',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            minWidth: '45px',
                            textAlign: 'center'
                          }}>
                            {Math.trunc(inputField.editor.height * 100)}%
                          </span>
                        </div>
                        <Slider
                          value={inputField.editor.height * 100}
                          min={5}
                          max={100}
                          step={1}
                          onChange={(event, newValue) => handleHeightSliderChange(event, newValue, index)}
                          sx={{
                            color: colors.primary.main,
                            height: 6,
                            '& .MuiSlider-thumb': {
                              width: 20,
                              height: 20,
                              backgroundColor: '#fff',
                              border: '3px solid currentColor',
                              '&:hover': {
                                boxShadow: '0 0 0 8px rgba(19, 36, 161, 0.16)'
                              }
                            },
                            '& .MuiSlider-track': {
                              border: 'none',
                              height: 6
                            },
                            '& .MuiSlider-rail': {
                              opacity: 0.3,
                              height: 6
                            }
                          }}
                        />
                      </div>
                    </div>
                    )}

                    {/* Options Section - Conditional (AIF-48) */}
                    {getFieldControlVisibility(inputField.editor.inputType, inputField.editor.renderType, inputField.options?.length).showOptions && (
                    <div style={{ marginBottom: '20px' }}>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={(event) => handleAddOption(event, index)}
                        fullWidth
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: '500',
                          padding: '10px',
                          borderColor: colors.primary.main,
                          color: colors.primary.main,
                          '&:hover': {
                            borderColor: colors.primary.dark,
                            backgroundColor: colors.state.hover
                          }
                        }}
                      >
                        {t('createTemplate.addOption')}
                      </Button>
                      {inputField.options.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {inputField.options.map((input, optionIndex) => (
                            <TextField
                              key={optionIndex}
                              value={inputField.options[optionIndex]}
                              onChange={e => handleOptionChange(e, index, optionIndex)}
                              placeholder={t('createTemplate.optionValuePlaceholder')}
                              size="small"
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  backgroundColor: '#ffffff'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </div>
                    )}

                    {/* Mandatory Checkbox - Conditional (AIF-48) */}
                    {getFieldControlVisibility(inputField.editor.inputType, inputField.editor.renderType, inputField.options?.length).showMandatory && (
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginBottom: '20px'
                    }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={inputField.mandatory}
                            onChange={(event) => handleMandatoryChange(event, index)}
                            sx={{
                              color: colors.primary.main,
                              '&.Mui-checked': {
                                color: colors.primary.main
                              }
                            }}
                          />
                        }
                        label={<span style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>{t('createTemplate.mandatoryField')}</span>}
                      />
                    </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginTop: '24px'
                    }}>
                      <Button
                        variant="contained"
                        onClick={() => switchEditorState(index)}
                        startIcon={<CheckIcon />}
                        fullWidth
                        sx={{
                          backgroundColor: '#28a745',
                          color: 'white',
                          padding: '12px',
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontWeight: '600',
                          fontSize: '15px',
                          '&:hover': {
                            backgroundColor: '#218838'
                          }
                        }}
                      >
                        {t('createTemplate.saveButton')}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => deleteField(index)}
                        aria-label={t('createTemplate.deleteFieldAriaLabel')}
                        sx={{
                          borderColor: '#dc3545',
                          color: '#dc3545',
                          padding: '12px',
                          borderRadius: '10px',
                          textTransform: 'none',
                          fontWeight: '600',
                          fontSize: '15px',
                          minWidth: '60px',
                          '&:hover': {
                            borderColor: '#c82333',
                            backgroundColor: 'rgba(220, 53, 69, 0.04)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </div>
                  </div>
                        </div>
                      )}
                    </div>
                  </Draggable>)
              }
            </React.Fragment> : null
        ))}
      </div>
      {selectedFile && <div className="m-1">
        {numPages > 1 && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            padding: '16px 0',
            justifyContent: 'space-between',
            maxWidth: '100%'
          }}>
            <Button
              variant="outlined"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              startIcon={<KeyboardArrowRightIcon />}
              sx={{
                borderColor: 'colors.primary.main',
                color: colors.primary.main,
                '&:hover': {
                  borderColor: 'colors.primary.dark',
                  backgroundColor: 'rgba(19, 36, 161, 0.04)'
                },
                '&:disabled': {
                  borderColor: '#ccc',
                  color: '#ccc'
                }
              }}
            >
              {t('createTemplate.previousPage')}
            </Button>
            <Typography sx={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#495057',
              minWidth: '120px',
              textAlign: 'center'
            }}>
              {t('createTemplate.pageOf', { current: currentPage, total: numPages })}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === numPages}
              endIcon={<KeyboardArrowLeftIcon />}
              sx={{
                borderColor: 'colors.primary.main',
                color: colors.primary.main,
                '&:hover': {
                  borderColor: 'colors.primary.dark',
                  backgroundColor: 'rgba(19, 36, 161, 0.04)'
                },
                '&:disabled': {
                  borderColor: '#ccc',
                  color: '#ccc'
                }
              }}
            >
              {t('createTemplate.nextPage')}
            </Button>
          </Box>
        )}
      </div>}
        </TabPanel>

        {/* Tab Panel 1: Required Documents */}
        <TabPanel value={activeTab} index={1}>
          {/* Help Text for Required Documents Tab */}
          <Box sx={{ marginBottom: 3, padding: 2, backgroundColor: '#fff3e0', borderRadius: 2, borderLeft: '4px solid #ff9800' }}>
            <Typography variant="body2" sx={{ color: '#e65100', lineHeight: 1.6 }}>
              📎 <strong>{t('createTemplate.tips.requiredDocsTitle')}</strong> {t('createTemplate.tips.requiredDocsText')}
            </Typography>
          </Box>

          <FileRequestForm requestedFiles={requestedFiles} setRequestedFiles={setRequestedFiles} />

          {/* Document Requirements Settings */}
          <Box sx={{ marginTop: 4, padding: 3, backgroundColor: '#f5f9ff', borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" sx={{ marginBottom: 1, color: colors.primary.main, fontWeight: 600 }}>
              {t('createTemplate.documentSettings.title')}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', marginBottom: 2, color: '#666' }}>
              {t('createTemplate.documentSettings.description')}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={requireID}
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label={t('createTemplate.requireIdLabel')}
            />
          </Box>
        </TabPanel>

        {/* Fixed Bottom Save Button - Always visible across all tabs */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderTop: '2px solid #e0e0e0',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
            zIndex: 100,
            marginTop: '32px'
          }}
        >
          {/* Help text above save button */}
          <Typography variant="caption" sx={{ marginBottom: 1.5, color: '#666', textAlign: 'center', maxWidth: 600 }}>
            ✅ {t('createTemplate.tips.saveButton')}
          </Typography>

          <Button
            variant="contained"
            onClick={saveInputFields}
            startIcon={<SaveIcon />}
            size="large"
            sx={{
              backgroundColor: colors.primary.main,
              color: 'white',
              fontWeight: '700',
              fontSize: '20px',
              padding: '16px 64px',
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(19, 36, 161, 0.4)',
              '&:hover': {
                backgroundColor: colors.primary.dark,
                boxShadow: '0 6px 20px rgba(19, 36, 161, 0.5)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {t('createTemplate.createDigitalCopy')}
          </Button>
        </Box>

        {loading &&
          <div className="loading-wrapper">
            <div className="loading">
              <InlineSpinner size="xl" />
            </div>
          </div>}

        {(linkFetched && templateLink !== "") && (
          <div className="modal-backdrop">
            <div className="modal" tabIndex="-1" role="dialog" style={{ display: 'block' }}>
              <div className="modal-dialog" role="document">
                <div className="modal-content">
                  <div className="modal-header" style={{ direction: 'ltr' }}>
                    <h5 className="modal-title">קישור המסמך</h5>
                    <button type="button" className="btn-close" onClick={() => setLinkFetched(false)}>
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <div className="modal-body" style={{ direction: 'rtl' }}>
                    <SimpleSnackbar templateLink={templateLink} name={templateLink} />
                    <p className="david pt-3">
                      הקישור לעיל מספק גרסת מהסמך שניתן למלא ולחתום על ידי הלקוח. <br />
                      באפשרותך לשתף קישור זה בקלות עם הלקוחות שלך. <br />
                      כאשר הלקוחות משלמים את המסמך על ידי מילוי הפרטים הנדרשים וחתימתו, העותק המלא של המסמך, יחד עם השדות שהוזנו, נשמר באופן אוטומטי בלוח ההגשות שלך.
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setLinkFetched(false)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </>}
    </>
  );

}

// Removed unused StyleSheet.create - was causing runtime error
// These styles were never referenced in the component
// const styles = StyleSheet.create({ ... });

const BootstrapButton = styled(Button)({
  boxShadow: 'none',
  textTransform: 'none',
  fontSize: 16,
  padding: '6px 12px',
  border: '1px solid',
  lineHeight: 1.5,
  backgroundColor: '#0063cc',
  borderColor: '#0063cc',
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  '&:hover': {
    backgroundColor: '#0069d9',
    borderColor: '#0062cc',
    boxShadow: 'none',
  },
  '&:active': {
    boxShadow: 'none',
    backgroundColor: '#0062cc',
    borderColor: '#005cbf',
  },
  '&:focus': {
    boxShadow: '0 0 0 0.2rem rgba(0,123,255,.5)',
  },
});

const options = [
  { label: 'שם הילד', value: 'childName' },
  { label: 'מספר תעודת תלמיד', value: 'childId' },
  { label: 'חתימה', value: 'signature' },
  { label: 'היום', value: 'day' },
  { label: 'החודש', value: 'month' },
  { label: 'השנה', value: 'year' },
  { label: 'שם הורה 1', value: 'parentName1' },
  { label: 'מספר תעודת הורה 1', value: 'parentId1' },
  { label: 'מספר פלפון הורה 1', value: 'phoneNumber1' },
  { label: 'שם הורה 2', value: 'parentName2' },
  { label: 'מספר תעודת הורה 2', value: 'parentId2' },
  { label: 'מספר פלפון הורה 2', value: 'phoneNumber2' },
  { label: 'שם גן', value: 'kindergarten' },
  { label: 'שנה עברית', value: 'hebrewYear' },
  { label: 'שם ילד פרטי', value: 'childFirstName' },
  { label: 'שם משפחה', value: 'childLastName' },
  { label: 'יום הולדת', value: 'dateOfBirth' },
  { label: 'ארץ לידה', value: 'countryOfBirth' },
  { label: 'שנת עליה', value: 'yearOfArrival' },
  { label: 'כתובת', value: 'address' },
  { label: 'מיקוד - ZIP', value: 'zip' },
  { label: 'תאריך לידה', value: 'dateOfBirth' },
  { label: 'אח ראשון', value: 'brother1' },
  { label: 'אח שני', value: 'brother2' },
  { label: 'אח שלישי', value: 'brother3' },
  { label: 'אח רביעי', value: 'brother4' },
  { label: 'מקצוע הורה ראשון', value: 'parentJob1' },
  { label: 'מספר פלפון הורה ראשון', value: 'parentHomeNumber1' },
  { label: 'אימייל הורה ראשון', value: 'parentEmailAddress1' },
  { label: 'מקצוע הורה שני', value: 'parentJob2' },
  { label: 'מספר פלפון הורה שני', value: 'parentHomeNumber2' },
  { label: 'אימייל הורה שני', value: 'parentEmailAddress2' },
  { label: 'שם קרוב משפחה ראשון', value: 'relativeName1' },
  { label: 'סטטוס קרבות משפחה ראשון', value: 'relativeStatus1' },
  { label: 'מספר פ. קרוב משפחה ראשון', value: 'relativeNumber1' },
  { label: 'שם קרוב משפחה שני', value: 'relativeName2' },
  { label: 'סטטוס קרבות משפחה שני', value: 'relativeStatus2' },
  { label: 'מספר פ. קרוב משפחה שני', value: 'relativeNumber2' },
  { label: 'האם קיימת בעיה בריאותית', value: 'healthIssueExist' },
  { label: 'בעיה בריאותית וטיפול בה', value: 'healthIssueAndSolution' },
  { label: 'אלרגיה לתרופות', value: 'allergyToMedication' },
  { label: 'אלרגיה לאוכל', value: 'allergyToFood' },
  { label: 'מחלות קודמות', value: 'pastDiseases' },
  { label: 'אלרגיות', value: 'allergies' },
  { label: 'האם קיבל חיסונים מלאים', value: 'receivedFullVaccination' },
  { label: 'חיסונים שלא קיבל הילד', value: 'nonReceivedVaccinations' },
  { label: 'קופת חולים', value: 'hmo' },
  { label: 'הערות', value: 'remarks' },
  { label: 'תאריך התחלת הנוכחות', value: 'attendanceStartingDate' },
  { label: 'מ- (כתובת)', value: 'from' },
  { label: 'תאריך חתימה (היום)', value: 'signingDate' },
  { label: 'שם כיתה', value: 'className' },
  { label: 'סכום תשלום חודשה', value: 'monthlyPayment' },
  { label: 'אמצעי תשלום', value: 'paymentMethod' },
  { label: 'האם ההורים מאשרים לצילום הילד פנימי', value: 'allowsPhotographingInternal' },
  { label: 'האם ההורים מאשרים לצילום הילד חוץ', value: 'allowsPhotographingExternal' },
  { label: 'שם החותם', value: 'approverName' },
  { label: 'סטטוס קרבות החותם', value: 'approverStatus' },
  { label: 'כתובת החותם', value: 'approverAddress' },
  { label: 'מס. פלפון החותם', value: 'approverPhoneNumber' },
  { label: 'שם רופא', value: 'doctorName' },
  { label: 'גן קודם', value: 'previousKindergarten' },
  { label: 'מקום עבודה של הורה 1', value: 'parentWorkPlace1' },
  { label: 'מקום עבודה של הורה 2', value: 'parentWorkPlace2' },
  { label: 'מספר פלפון בעבודה הורה 1', value: 'parentWorkPhoneNumber1' },
  { label: 'מספר פלפון בעבודה הורה 2', value: 'parentWorkPhoneNumber2' },
  { label: 'מספר סידורי של הילד בין אחיו', value: 'childOrderBetweenSiblings' },
  { label: 'שם איש קשר למצב חירום', value: 'emergencyContactName' },
  { label: 'מס איש קשר למצב חירום', value: 'emergencyContactNumber' },
  { label: 'אחר', value: 'other' },
  { label: 'רשימה נפתחת', value: 'select' },
];

// const inputTypes = [
//   "day",
//   "month",
//   "year",
//   "childName",
//   "childId",
//   "parentName1",
//   "parentId1",
//   "phoneNumber1",
//   "parentName2",
//   "parentId2",
//   "phoneNumber2",
//   "kindergarten",
//   "hebrewYear",
//   "childFirstName",
//   "childLastName",
//   "dateOfBirth",
//   "countryOfBirth",
//   "yearOfArrival",
//   "address",
//   "zip",
//   "brother1",
//   "brother2",
//   "brother3",
//   "brother4",
//   "parentJob1",
//   "parentHomeNumber1",
//   "parentEmailAddress1",
//   "parentJob2",
//   "parentHomeNumber2",
//   "parentEmailAddress2",
//   "relativeName1",
//   "relativeStatus1",
//   "relativeNumber1",
//   "relativeName2",
//   "relativeStatus2",
//   "relativeNumber2",
//   "healthIssueExist",
//   "healthIssueAndSolution",
//   "allergyToMedication",
//   "allergyToFood",
//   "pastDiseases",
//   "allergies",
//   "receivedFullVaccination",
//   "nonReceivedVaccinations",
//   "hmo",
//   "remarks",
//   "attendanceStartingDate",
//   "from",
//   "signingDate",
//   "className",
//   "monthlyPayment",
//   "paymentMethod",
//   "allowsPhotographingInternal",
//   "allowsPhotographingExternal",
//   "approverName",
//   "approverStatus",
//   "approverAddress",
//   "approverPhoneNumber",
//   "signature1",
//   "signature2",
//   "temp1",
//   "temp2",
//   "temp3",
//   "temp4",

// ];

// const data = {
//   day: "",
//   month: "",
//   year: "",
//   childName: "",
//   childId: "",
//   parentName1: "",
//   parentId1: "",
//   phoneNumber1: "",
//   parentName2: "",
//   parentId2: "",
//   phoneNumber2: "",
//   kindergarten: "",
//   hebrewYear: "",
//   childFirstName: "",
//   childLastName: "",
//   dateOfBirth: "",
//   countryOfBirth: "",
//   yearOfArrival: "",
//   address: "",
//   zip: "",
//   brother1: "",
//   brother2: "",
//   brother3: "",
//   brother4: "",
//   parentJob1: "",
//   parentHomeNumber1: "",
//   parentEmailAddress1: "",
//   parentJob2: "",
//   parentHomeNumber2: "",
//   parentEmailAddress2: "",
//   relativeName1: "",
//   relativeStatus1: "",
//   relativeNumber1: "",
//   relativeName2: "",
//   relativeStatus2: "",
//   relativeNumber2: "",
//   healthIssueExist: "false",
//   healthIssueAndSolution: "",
//   allergyToMedication: "",
//   allergyToFood: "",
//   pastDiseases: "",
//   allergies: "",
//   receivedFullVaccination: "true",
//   nonReceivedVaccinations: "",
//   hmo: "",
//   remarks: "",
//   attendanceStartingDate: "",
//   from: "",
//   signingDate: "",
//   className: "",
//   monthlyPayment: "",
//   paymentMethod: "12-checks",
//   allowsPhotographingInternal: "false",
//   allowsPhotographingExternal: "false",
//   approverName: "",
//   approverStatus: "",
//   approverAddress: "",
//   approverPhoneNumber: "",

//   signature1: "data:image/png;base64,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",
//   signature2: "data:image/png;base64,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",

// };